/**
 * NotificationCard — Wave 8 (FRONTEND).
 *
 * Adaptive-card-style row used inside NotificationBell's dropdown. Composed
 * by the bell, never used directly elsewhere.
 *
 * Layout:
 *   [icon]  [title (bold) + age]
 *           [body, clamped to 2 lines / ~80 chars]
 *           [optional CTA chip]      [kebab menu]
 *
 * Click on the card body navigates (via the supplied callback). Click on the
 * kebab opens an inline menu with Mark read / Dismiss. No GUIDs are exposed
 * in user-facing copy (Bill 2026-04-25 binding).
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Bell, MoreHorizontal } from 'lucide-react';
import type { InAppNotification } from '@retail-ops/api-client';

export interface NotificationCardProps {
  notification: InAppNotification;
  /** Called when the card body is activated (click / Enter). */
  onNavigate?: (notification: InAppNotification) => void;
  /** Mark a single row read. */
  onMarkRead?: (id: string) => void;
  /** Dismiss a single row. */
  onDismiss?: (id: string) => void;
}

const BODY_TRUNC_CHARS = 80;

/**
 * Compact relative-time formatter — "2m", "3h", "Yesterday", "Apr 22".
 * No external deps; the format matches Outlook/Teams conventions.
 */
function formatAge(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Math.max(0, now - then);
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d`;
  // Drop to month/day; matches the rest of v3's date affordances.
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1).trimEnd()}…`;
}

export function NotificationCard({
  notification,
  onNavigate,
  onMarkRead,
  onDismiss,
}: NotificationCardProps): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const isUnread = !notification.readAt;
  const isHigh = notification.priority === 'high';

  // Outside-click for kebab menu — close when click lands outside the card.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (ev: MouseEvent): void => {
      if (cardRef.current && !cardRef.current.contains(ev.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const handleNavigate = (): void => {
    if (isUnread) onMarkRead?.(notification.id);
    onNavigate?.(notification);
  };

  const handleKey = (ev: KeyboardEvent<HTMLDivElement>): void => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      handleNavigate();
    }
  };

  const className = [
    'notif-card',
    isUnread ? 'is-unread' : '',
    isHigh ? 'is-priority-high' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={cardRef}
      className={className}
      role="button"
      tabIndex={0}
      onClick={handleNavigate}
      onKeyDown={handleKey}
      aria-label={`${isUnread ? 'Unread notification: ' : ''}${notification.titleText}`}
    >
      <span className="notif-card__icon" aria-hidden>
        <Bell size={14} />
      </span>

      <div className="notif-card__title">
        {isUnread && <span className="notif-card__unread-dot" aria-hidden />}
        <span>{notification.titleText}</span>
      </div>

      <div className="notif-card__body">
        {truncate(notification.bodyText, BODY_TRUNC_CHARS)}
      </div>

      {notification.ctaLabel && notification.navigateTo && (
        <button
          type="button"
          className="notif-card__cta"
          onClick={(e) => {
            e.stopPropagation();
            handleNavigate();
          }}
        >
          {notification.ctaLabel}
        </button>
      )}

      <span className="notif-card__meta">
        <span title={new Date(notification.createdAt).toLocaleString()}>
          {formatAge(notification.createdAt)}
        </span>
        <button
          type="button"
          className="notif-card__kebab"
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen ? 'true' : 'false'}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
        >
          <MoreHorizontal size={14} aria-hidden />
        </button>
      </span>

      {menuOpen && (
        <div className="notif-card__kebab-menu" role="menu">
          {isUnread && onMarkRead && (
            <button
              type="button"
              role="menuitem"
              className="notif-card__kebab-item"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                onMarkRead(notification.id);
              }}
            >
              Mark as read
            </button>
          )}
          {onDismiss && (
            <button
              type="button"
              role="menuitem"
              className="notif-card__kebab-item"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
                onDismiss(notification.id);
              }}
            >
              Dismiss
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationCard;
